<script setup lang="ts">
/**
 * 全局确认弹窗容器 —— 在 AppLayout 挂一次,渲染 useConfirm() 的当前请求。
 * 替代 window.confirm:视觉跟应用主题一致、危险操作标红、Esc/遮罩取消、Enter 确认。
 */
import { onMounted, onBeforeUnmount } from 'vue'
import { useConfirm } from '@/composables/useConfirm'

const { state, _settle } = useConfirm()

function onConfirm() {
  _settle(true)
}
function onCancel() {
  _settle(false)
}

function onKeydown(e: KeyboardEvent) {
  if (!state.open) return
  if (e.key === 'Escape') {
    e.preventDefault()
    onCancel()
  } else if (e.key === 'Enter') {
    e.preventDefault()
    onConfirm()
  }
}
onMounted(() => window.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
</script>

<template>
  <div v-if="state.open" class="overlay" @click.self="onCancel">
    <div class="modal" role="alertdialog" aria-modal="true">
      <div class="title">{{ state.title }}</div>
      <p class="message">{{ state.message }}</p>
      <div class="actions">
        <button type="button" class="btn-cancel" @click="onCancel">{{ state.cancelText }}</button>
        <button
          type="button"
          class="btn-confirm"
          :class="{ danger: state.danger }"
          @click="onConfirm"
        >
          {{ state.confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  /* 高于普通 modal(100)和 toast(200):确认可能从 modal 内触发,要盖在最上 */
  z-index: 300;
  padding: 24px;
}
.modal {
  width: 400px;
  max-width: 100%;
  background: var(--color-background-primary);
  border-radius: var(--border-radius-lg);
  border: 0.5px solid var(--color-border-tertiary);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  padding: 20px;
}
.title {
  font-size: var(--fs-page);
  font-weight: var(--fw-bold);
  color: var(--color-text-primary);
  margin-bottom: 8px;
}
.message {
  margin: 0 0 18px;
  font-size: var(--fs-control);
  line-height: 1.6;
  color: var(--color-text-secondary);
  white-space: pre-line;
  word-break: break-word;
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.actions button {
  padding: 6px 16px;
  font-size: var(--fs-control);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  font-family: inherit;
  border: var(--border-width-control) solid transparent;
}
.btn-cancel {
  background: transparent;
  border-color: var(--color-border-control);
  color: var(--color-text-secondary);
}
.btn-cancel:hover {
  background: var(--color-background-secondary);
  color: var(--color-text-primary);
}
.btn-confirm {
  background: var(--color-background-info);
  color: var(--color-text-info);
}
.btn-confirm.danger {
  background: var(--color-background-danger);
  color: var(--color-text-danger);
}
.btn-confirm:hover {
  opacity: 0.85;
}
</style>
